import { useSyncExternalStore } from "react";

/**
 * Per-query singleton that wraps a single matchMedia listener.
 * All hook instances sharing the same query string read from the
 * same snapshot, so React can batch them into one render pass
 * instead of N independent state updates.
 */
type MediaQueryStore = {
    subscribe: (cb: () => void) => () => void;
    getSnapshot: () => boolean;
    getServerSnapshot: () => boolean;
};

const stores = new Map<string, MediaQueryStore>();

function getStore(query: string): MediaQueryStore {
    let store = stores.get(query);
    if (store) return store;

    if (typeof window === "undefined") {
        store = {
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            subscribe: () => () => {},
            getSnapshot: () => false,
            getServerSnapshot: () => false,
        };
        stores.set(query, store);
        return store;
    }

    const mql = window.matchMedia(query);
    const listeners = new Set<() => void>();

    mql.addEventListener("change", () => {
        for (const cb of listeners) cb();
    });

    store = {
        subscribe(cb: () => void) {
            listeners.add(cb);
            return () => listeners.delete(cb);
        },
        getSnapshot() {
            return mql.matches;
        },
        getServerSnapshot() {
            return false;
        },
    };

    stores.set(query, store);
    return store;
}

/**
 * Returns true when the viewport matches the given media query string.
 *
 * Uses a module-level singleton per query so that multiple components
 * subscribing to the same query share one matchMedia listener and
 * receive the same snapshot in a single React render pass.
 */
export function useMediaQuery(query: string): boolean {
    const store = getStore(query);
    return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getServerSnapshot);
}

/**
 * Clears cached media query stores so tests that mock window.matchMedia
 * get a fresh store with the mocked implementation.
 */
export function resetMediaQueryStores(): void {
    stores.clear();
}
